import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { FiX, FiUsers, FiShield, FiSearch, FiCheckCircle, FiFileText } from "react-icons/fi";
import { GoPackage } from "react-icons/go";
import api from "../services/api";
import { toast } from "react-toastify";
import { SYSTEM_MODULES, SECTION_LABELS } from "../config/modules";
import { tokens, surface, hairline } from "../styles/designTokens";
import { useAuth } from "../contexts/AuthContext";

const EditProjectModal = ({ isOpen, onClose, project, onSave }) => {
  const { refreshCurrentProject } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [saving, setSaving] = useState(false);
  
  // Users tab state
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  
  // Permissions tab state
  const [permissions, setPermissions] = useState({});

  // Dados Fiscais tab state (Bloco A do simulador de Reforma Tributária:
  // Regime, Filiais e Atividade — antes disso, eram texto fixo de exemplo)
  const [regime, setRegime] = useState("");
  const [filiais, setFiliais] = useState("");
  const [atividade, setAtividade] = useState("");
  // Ata reunião (Diogo): flag de atividade industrial/equiparado para fins
  // de IPI/IS + CNAE consultável via API pública da Receita.
  const [cnae, setCnae] = useState("");
  const [atividadeIndustrial, setAtividadeIndustrial] = useState(false);
  const [loadingCnae, setLoadingCnae] = useState(false);

  // Load users and initialize state when modal opens
  useEffect(() => {
    if (isOpen && project) {
      fetchUsers();
      initializePermissions();
      setSelectedUsers(project.users || []);
      setRegime(project.regime || "");
      setFiliais(project.filiais || "");
      setAtividade(project.atividade || "");
      setCnae(project.cnae || "");
      setAtividadeIndustrial(project.atividadeIndustrial === true);
    }
  }, [isOpen, project]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get("/users");
      const allUsers = response.data.users || [];
      
      // Filter out god users (same as NewProjectModal)
      const filteredUsers = allUsers.filter(user => user.cargo !== "god");
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoadingUsers(false);
    }
  };

  const initializePermissions = () => {
    if (project?.permissions) {
      // Converter formato legado (string) para booleano se necessário
      const normalized = {};
      SYSTEM_MODULES.forEach((module) => {
        const val = project.permissions[module.id];
        if (typeof val === 'boolean') {
          normalized[module.id] = val;
        } else if (typeof val === 'string') {
          normalized[module.id] = val !== 'none' && val !== '';
        } else {
          normalized[module.id] = false;
        }
      });
      setPermissions(normalized);
    } else {
      const defaultPermissions = {};
      SYSTEM_MODULES.forEach((module) => {
        defaultPermissions[module.id] = false;
      });
      setPermissions(defaultPermissions);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update users
      await api.put(`/projects/${project.id}/users`, {
        users: selectedUsers,
      });

      // Update permissions
      await api.put(`/projects/${project.id}/permissions`, {
        permissions,
      });

      // Update fiscal info (Regime/Filiais/Atividade/CNAE/Indústria)
      await api.put(`/projects/${project.id}/fiscal-info`, {
        regime,
        filiais,
        atividade,
        cnae,
        atividadeIndustrial,
      });

      toast.success("Projeto atualizado com sucesso!");

      // Se o projeto editado é o projeto atualmente selecionado, atualiza o
      // cache do AuthContext (currentProject.permissions) — sem isso, o menu
      // lateral continua mostrando o teto de permissões antigo até o
      // usuário trocar de projeto e voltar.
      if (project?.id === localStorage.getItem('vidal_selected_project')) {
        refreshCurrentProject(project.id);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error("Erro ao salvar projeto:", error);
      toast.error("Erro ao salvar projeto");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setActiveTab("users");
    setUserSearchTerm("");
    onClose();
  };

  // Consulta o CNAE principal do CNPJ do projeto via backend (BrasilAPI com
  // cache) — ata: Diogo confirmou que o comprovante da Receita expõe o CNAE.
  const handleBuscarCnae = async () => {
    const cnpj = (project?.cnpj || "").replace(/\D/g, "");
    if (!cnpj) {
      toast.warn("Projeto sem CNPJ cadastrado.");
      return;
    }
    setLoadingCnae(true);
    try {
      const resp = await api.get(`/cnpj/cnae/${cnpj}`);
      const data = resp?.data?.data || resp?.data;
      if (data?.cnae) {
        setCnae(data.cnae);
        if (data.cnaeDescricao) setAtividade(data.cnaeDescricao);
        if (typeof data.atividadeIndustrialSugerida === "boolean") {
          setAtividadeIndustrial(data.atividadeIndustrialSugerida);
        }
        toast.success(`CNAE ${data.cnae}${data.cnaeDescricao ? ` — ${data.cnaeDescricao}` : ""}`);
      } else {
        toast.warn("CNAE não encontrado para este CNPJ.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao consultar CNAE.");
    } finally {
      setLoadingCnae(false);
    }
  };

  if (!isOpen || !project) return null;

  // Filter users by search term
  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  return ReactDOM.createPortal(
    <Overlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ProjectInfo>
            <ProjectIcon style={{ color: project.color || "#10b981" }}>
              <GoPackage />
            </ProjectIcon>
            <ProjectDetails>
              <ProjectName>{project.nome}</ProjectName>
              <ProjectCNPJ>{project.cnpj}</ProjectCNPJ>
            </ProjectDetails>
          </ProjectInfo>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        {/* Tabs */}
        <TabsContainer>
          <Tab
            active={activeTab === "users"}
            onClick={() => setActiveTab("users")}
          >
            <FiUsers />
            Usuários
          </Tab>
          <Tab
            active={activeTab === "permissions"}
            onClick={() => setActiveTab("permissions")}
          >
            <FiShield />
            Acessos
          </Tab>
          <Tab
            active={activeTab === "fiscal"}
            onClick={() => setActiveTab("fiscal")}
          >
            <FiFileText />
            Dados Fiscais
          </Tab>
        </TabsContainer>

        {/* Content */}
        <ModalContent>
          {activeTab === "users" && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiUsers />
                </div>
                <div className="info-content">
                  <h3>Gerenciar Usuários do Projeto</h3>
                  <p>
                    Selecione os usuários que terão acesso a este projeto.
                  </p>
                </div>
              </InfoBox>

              <InputSection>
                <div className="section-header">
                  <label>Buscar Usuários</label>
                  {selectedUsers.length > 0 && (
                    <SelectedCount>
                      {selectedUsers.length} usuário{selectedUsers.length > 1 ? 's' : ''} selecionado{selectedUsers.length > 1 ? 's' : ''}
                    </SelectedCount>
                  )}
                </div>
                <SearchInput>
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                </SearchInput>
              </InputSection>

              <UsersList>
                {loadingUsers ? (
                  <LoadingMessage>Carregando usuários...</LoadingMessage>
                ) : filteredUsers.length === 0 ? (
                  <EmptyMessage>Nenhum usuário encontrado</EmptyMessage>
                ) : (
                  filteredUsers.map(user => (
                    <UserCard
                      key={user.id}
                      selected={selectedUsers.includes(user.id)}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="user-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <h4>{user.name}</h4>
                        <p>{user.email}</p>
                      </div>
                      <div className="user-role">
                        {user.cargo}
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <div className="check-icon">
                          <FiCheckCircle />
                        </div>
                      )}
                    </UserCard>
                  ))
                )}
              </UsersList>
            </>
          )}

          {activeTab === "permissions" && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiShield />
                </div>
                <div className="info-content">
                  <h3>Módulos Disponíveis no Projeto</h3>
                  <p>
                    Define quais módulos ficam acessíveis para os usuários deste projeto. Cada usuário pode ter suas permissões individuais dentro destes limites.
                  </p>
                </div>
              </InfoBox>

              <ToggleHeader>
                <span>Módulo</span>
                <button
                  onClick={() => {
                    const allOn = SYSTEM_MODULES.every(m => permissions[m.id] === true);
                    const updated = {};
                    SYSTEM_MODULES.forEach(m => { updated[m.id] = !allOn; });
                    setPermissions(updated);
                  }}
                >
                  {SYSTEM_MODULES.every(m => permissions[m.id] === true) ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </ToggleHeader>

              <PermissionsList>
                {['dashboards', 'tools', 'settings'].map(section => {
                  const sectionModules = SYSTEM_MODULES.filter(m => m.section === section);
                  if (sectionModules.length === 0) return null;
                  return (
                    <PermissionSection key={section}>
                      <h5>{SECTION_LABELS[section]}</h5>
                      {sectionModules.map(module => {
                        const Icon = module.icon;
                        const isEnabled = permissions[module.id] === true;
                        return (
                          <ToggleItem
                            key={module.id}
                            $enabled={isEnabled}
                            $color={module.color}
                            onClick={() => setPermissions({ ...permissions, [module.id]: !isEnabled })}
                          >
                            <div className="toggle-label">
                              <Icon className="toggle-icon" />
                              <span>{module.label}</span>
                            </div>
                            <div className={`toggle-switch ${isEnabled ? 'on' : 'off'}`}>
                              <div className="toggle-knob" />
                            </div>
                          </ToggleItem>
                        );
                      })}
                    </PermissionSection>
                  );
                })}
              </PermissionsList>
            </>
          )}

          {activeTab === "fiscal" && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiFileText />
                </div>
                <div className="info-content">
                  <h3>Dados Fiscais do Cliente</h3>
                  <p>
                    Regime tributário, filiais e atividade do cliente. Esses dados alimentam o Bloco A
                    (Dados do Cliente) da tela Reforma Tributária. Deixe em branco para manter o texto de
                    exemplo até o cadastro ser preenchido.
                  </p>
                </div>
              </InfoBox>

              <FieldGroup>
                <label>Regime Tributário</label>
                <FieldInput
                  type="text"
                  placeholder="Ex: Lucro Real — PIS/COFINS Não-Cumulativo"
                  value={regime}
                  onChange={(e) => setRegime(e.target.value)}
                />
              </FieldGroup>

              <FieldGroup>
                <label>Filiais</label>
                <FieldInput
                  type="text"
                  placeholder="Ex: 1 unidade"
                  value={filiais}
                  onChange={(e) => setFiliais(e.target.value)}
                />
              </FieldGroup>

              <FieldGroup>
                <label>Atividade</label>
                <FieldInput
                  type="text"
                  placeholder="Ex: Comércio de Veículos, Autopeças e Oficina Mecânica"
                  value={atividade}
                  onChange={(e) => setAtividade(e.target.value)}
                />
              </FieldGroup>

              <FieldGroup>
                <label>CNAE Principal (Receita Federal)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <FieldInput
                    type="text"
                    placeholder="Ex: 4511-1/01"
                    value={cnae}
                    onChange={(e) => setCnae(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <CnaeButton
                    type="button"
                    onClick={handleBuscarCnae}
                    disabled={loadingCnae}
                  >
                    {loadingCnae ? "Consultando…" : "Buscar CNAE"}
                  </CnaeButton>
                </div>
              </FieldGroup>

              <ToggleItem
                $enabled={atividadeIndustrial}
                $color="#f59e0b"
                onClick={() => setAtividadeIndustrial(!atividadeIndustrial)}
              >
                <div className="toggle-label">
                  <span>Indústria ou equiparado à indústria (fins de IPI/IS)</span>
                </div>
                <div className={`toggle-switch ${atividadeIndustrial ? 'on' : 'off'}`}>
                  <div className="toggle-knob" />
                </div>
              </ToggleItem>
            </>
          )}
        </ModalContent>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={handleClose}>Cancelar</CancelButton>
          <SaveButton onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar Alterações"}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

/* ============================================================
 * STYLED COMPONENTS — design system tokens
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: 2em;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 580px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const ProjectIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${tokens.radiusMd};
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
  flex-shrink: 0;
`;

const ProjectDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  &::before {
    content: "PROJETOS · EDITAR";
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.4;
    margin-bottom: 2px;
  }
`;

const ProjectName = styled.h3`
  margin: 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.015em;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`;

const ProjectCNPJ = styled.span`
  ${mono};
  font-size: 0.7em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  letter-spacing: 0.02em;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${tokens.radiusMd};
  border: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 1)};
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  font-size: 1em;
  opacity: 0.65;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    border-color: ${({ theme }) => hairline(theme, true)};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;
`;

const Tab = styled.button`
  ${mono};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 16px;
  border: none;
  background: transparent;
  border-bottom: 2px solid ${({ theme, active }) => (active ? tokens.brand : 'transparent')};
  color: ${({ theme, active }) => (active ? tokens.brand : theme.text)};
  opacity: ${({ active }) => (active ? 1 : 0.45)};
  font-size: 0.66em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  font-family: ${tokens.fontMono};

  &:hover { opacity: ${({ active }) => (active ? 1 : 0.75)}; }

  svg { font-size: 1.2em; }
`;

const ModalContent = styled.div`
  flex: 1;
  padding: 18px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusMd};

  .info-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${tokens.brandRing};
    color: ${tokens.brand};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95em;
    flex-shrink: 0;
  }

  .info-content {
    flex: 1;
    min-width: 0;

    h3 {
      margin: 0 0 3px 0;
      font-size: 0.88em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      line-height: 1.5;
      letter-spacing: -0.005em;
    }
  }
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  label {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
  }
`;

const FieldInput = styled.input`
  padding: 9px 12px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  letter-spacing: -0.005em;
  outline: none;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:focus { border-color: ${tokens.brandRing}; }
  &::placeholder { opacity: 0.4; }
`;

const CnaeButton = styled.button`
  padding: 9px 14px;
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusMd};
  color: ${tokens.brand};
  font-family: ${tokens.fontSans};
  font-size: 0.75em;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  white-space: nowrap;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover:not(:disabled) {
    background: ${tokens.brand};
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:focus-within { border-color: ${tokens.brandRing}; }

  svg {
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    font-size: 0.9em;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.text};
    font-family: ${tokens.fontSans};
    font-size: 0.82em;
    letter-spacing: -0.005em;
    outline: none;

    &::placeholder { opacity: 0.4; }
  }
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UserCard = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: ${({ selected, theme }) => selected ? surface(theme, 1) : 'transparent'};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  opacity: ${({ selected }) => selected ? 1 : 0.65};

  &:hover {
    border-color: ${({ theme }) => hairline(theme, true)};
    background: ${({ theme }) => surface(theme, 1)};
    opacity: 1;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${({ selected }) => selected ? tokens.brand : '#6b7280'};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    ${mono};
    font-weight: 600;
    font-size: 0.78em;
    flex-shrink: 0;
    transition: background ${tokens.durBase} ${tokens.easeOut};
  }

  .user-info {
    min-width: 0;

    h4 {
      margin: 0 0 1px 0;
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.66em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .user-role {
    ${mono};
    padding: 2px 8px;
    background: ${({ theme }) => surface(theme, 2)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusPill};
    font-size: 0.58em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .check-icon {
    color: ${tokens.brand};
    font-size: 1em;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 32px;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  ${mono};
  font-size: 0.78em;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 32px;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  ${mono};
  font-size: 0.78em;
`;

const SelectedCount = styled.div`
  ${mono};
  padding: 3px 10px;
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusPill};
  color: ${tokens.brand};
  font-size: 0.6em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const PermissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PermissionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  h5 {
    ${eyebrow};
    margin: 0 0 4px 0;
    font-size: 0.55em;
    opacity: 0.45;
  }
`;

const ToggleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  span {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
  }

  button {
    ${mono};
    background: transparent;
    border: 1px solid ${({ theme }) => hairline(theme)};
    color: ${({ theme }) => theme.text};
    opacity: 0.75;
    padding: 5px 10px;
    border-radius: ${tokens.radiusSm};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover {
      opacity: 1;
      border-color: ${tokens.brandRing};
      color: ${tokens.brand};
      background: ${({ theme }) => surface(theme, 1)};
    }
  }
`;

const ToggleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  background: ${({ $enabled, theme }) => $enabled ? tokens.brandSoft : surface(theme, 1)};
  border: 1px solid ${({ $enabled, theme }) => $enabled ? tokens.brandRing : hairline(theme)};
  border-radius: ${tokens.radiusSm};
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover {
    border-color: ${({ theme }) => hairline(theme, true)};
    background: ${({ $enabled, theme }) => $enabled ? tokens.brandSoft : surface(theme, 0)};
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;

    .toggle-icon {
      font-size: 1em;
      color: ${({ $enabled }) => $enabled ? tokens.brand : 'inherit'};
      opacity: ${({ $enabled }) => $enabled ? 1 : 0.6};
      transition: all ${tokens.durBase} ${tokens.easeOut};
    }

    span {
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
    }
  }

  .toggle-switch {
    width: 34px;
    height: 20px;
    border-radius: 10px;
    position: relative;
    transition: background ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    &.on { background: ${tokens.brand}; }
    &.off { background: ${({ theme }) => surface(theme, 2)}; }

    .toggle-knob {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: white;
      position: absolute;
      top: 3px;
      transition: left ${tokens.durBase} ${tokens.easeOut};
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    &.on .toggle-knob { left: 17px; }
    &.off .toggle-knob { left: 3px; }
  }
`;

const ModalFooter = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: ${tokens.radiusMd};
  border: 1px solid ${({ theme }) => hairline(theme)};
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  opacity: 0.85;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: ${tokens.radiusMd};
  border: 1px solid ${tokens.brand};
  background: ${tokens.brand};
  color: white;
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export default EditProjectModal;
